<template>
  <b-modal :id="id" :title="id" size="xl" lazy centered
           body-class="w-100" hide-header hide-footer @show="load">
    <template #default>
      <b-overlay :show="true" bg-color="white" opacity="1" style="min-height: 100px;">
        <template v-if="!studentEvents.loading">
            <b-table-simple v-for="chaperone in computedChaperones" :key="chaperone.id" responsive caption-top class="d-none d-print-block"><!---->
              <caption>
                <div class="d-flex justify-content-center gap-1">
                  <b-img :src="logo" class="print-logo" alt="Logo" style="max-width: 50px; max-height: 50px;" />
                  <div class="text-body d-flex flex-column align-self-center">
                    <div class="font-weight-bold font-medium-4">Bed Check Roster</div>
                    <div class="font-weight-bold font-small-4">All-State - {{ $store.getters['settings/getCurrentYear'] }}</div>
                  </div>
                </div>
              </caption>
              <b-thead>
                <b-tr>
                  <b-th colspan="4" class="font-weight-bolder font-medium-1 py-0" :class="{'text-danger': !chaperone.name}">
                    {{ getFullName(chaperone, { lnf: true }) }}
                  </b-th>
                  <b-th colspan="4" class="font-weight-bolder font-medium-1 text-right py-0" :class="{'text-danger': (!chaperone.hotelId || !chaperone.hotel || !chaperone.hotel.name)}">
                    <template v-if="!chaperone.hotelId || !chaperone.hotel">No Hotel</template>
                    <template v-else-if="!chaperone.hotel.name">Unnamed Hotel</template>
                    <template v-else>
                      {{ chaperone.hotel.name }}
                    </template>
                  </b-th>
                </b-tr>
                <b-tr>
                  <b-th colspan="8" class="pb-2">{{ studentEvents.items.filter(studentEvent => studentEvent.chaperoneId === chaperone.id).length || 0 }} Students</b-th>
                </b-tr>
                <b-tr>
                  <b-th class="col-10-percent">Room</b-th>
                  <b-th class="col-20-percent">Student</b-th>
                  <b-th class="col-10-percent">Sex</b-th>
                  <b-th class="col-10-percent">Gender</b-th>
                  <b-th class="col-20-percent">Ensemble</b-th>
                  <b-th class="col-10-percent text-center">Thursday</b-th>
                  <b-th class="col-10-percent text-center">Friday</b-th>
                  <b-th class="col-10-percent text-center">Saturday</b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr v-for="event in getStudentEventsForChaperone(chaperone)" :key="event.id"
                      :data="application = event.student ? getApplicationWithSelection(event.student.applications) : null">
                  <b-td>
                    <span v-if="event.room">
                       {{ event.room }}
                    </span>
                    <span v-else class="text-danger">
                       No Room
                    </span>
                  </b-td>
                  <b-td>
                    <span v-if="event.student">
                      {{ getFullName(event.student, { lnf: true, usePreferred: true }) }}
                    </span>
                    <span v-else class="text-danger">
                      No Student
                    </span>
                  </b-td>
                  <b-td>
                    <span v-if="event.student" class="text-capitalize">
                       {{ event.student.sex }}
                    </span>
                    <span v-else class="text-danger">
                      Unknown Sex
                    </span>
                  </b-td>
                  <b-td>
                    <span v-if="event.student" class="text-capitalize">
                       {{ event.student.gender }}
                    </span>
                    <span v-else class="text-danger">
                      Unknown Gender
                    </span>
                  </b-td>
                  <b-td>
                    <span v-if="application && application.selection && application.selection.ensemble">
                       {{ application.selection.ensemble.name }}
                    </span>
                    <span v-else class="text-danger">
                      Unknown Ensemble
                    </span>
                  </b-td>
                  <b-td class="text-center">
                    <b-icon :icon="event.checkin.thursday ? 'check-square' : 'square'"/>
                  </b-td>
                  <b-td class="text-center">
                    <b-icon :icon="event.checkin.friday ? 'check-square' : 'square'"/>
                  </b-td>
                  <b-td class="text-center">
                    <b-icon :icon="event.checkin.saturday ? 'check-square' : 'square'"/>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </template>
        <template v-else>
          <b-card class="shadow-none m-0 p-0">
            <b-card-body>
              <!-- Card needed to give overlay some dimension -->
            </b-card-body>
          </b-card>
        </template>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import vSelect from 'vue-select'
import PageLayout from '@/components/PageLayout.vue';
import notify from '@/mixins/notify.mixin';
import OverlayLoading from '@/components/OverlayLoading.vue';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import ApplicationDetails from '@/views/all-state/application/ApplicationDetails.vue';
import ApplicationStudent from '@/views/all-state/application/ApplicationStudent.vue';
import ApplicationQuestions from '@/views/all-state/application/ApplicationQuestions.vue';
import ApplicationGrading from '@/views/all-state/application/ApplicationGrading.vue';
import popper from '@/mixins/popper.mixin';
import BCardActions from '@core/components/b-card-actions/BCardActions.vue';
import TableRowOptions from '@/components/TableRowOptions.vue';
import settingsMixin from '@/mixins/settings.mixin';
import { getStudentEvents } from '@/views/events/service';
import events from '@/mixins/event.mixin';

export default {
  name: 'CheckinModal',
  components: {
    TableRowOptions,
    BCardActions,
    ApplicationGrading,
    ApplicationDetails,
    ApplicationStudent,
    ApplicationQuestions,
    OverlayLoading,
    PageLayout,
    vSelect,
    VuePerfectScrollbar
  },
  mixins: [notify, popper, settingsMixin, events],
  props: {
    id: {
      type: String,
      required: false,
      default: null
    },
    chaperones: {
      type: Array,
      required: true,
      default: () => []
    },
    events: {
      type: Array,
      default: () => []
    },
    hotels: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    sorting: {
      type: Object,
      default: () => ({
        by: ['room', 'student.name.last', 'student.name.first'],
        desc: false,
        options: [
          { label: 'Room', value: 'room'},
          { label: 'First Name', value: 'student.name.first'},
          { label: 'Last Name', value: 'student.name.last'},
        ]
      })
    }
  },
  data() {
    /* eslint-disable global-require */
    return {
      logo: null,
      instrument: null,
      ensembles: {
        loading: true,
        items: []
      },
      studentEvents: {
        loading: true,
        items: [ ],
      },
    }
  },
  computed: {
    computedChaperones() {
      const items = this.chaperones?.filter(
        ch => !(this.filters?.hotel && this.filters.hotel !== ch?.hotelId)
      ) ?? [];

      return items.slice().sort((a, b) => {
        // 1) Hotel name
        const h = this.compareString(a?.hotel?.name, b?.hotel?.name);
        if (h !== 0) return h;

        // 2) Chaperone last name
        const ln = this.compareString(a?.name?.last, b?.name?.last);
        if (ln !== 0) return ln;

        // 3) Chaperone first name
        return this.compareString(a?.name?.first, b?.name?.first);
      });
    }
  },
  watch: {
    events: {
      deep: true,
      handler(value) {
        this.studentEvents.items = value
      }
    },
  },
  mounted() {
    window.addEventListener('afterprint', this.handleAfterPrint);
  },
  beforeDestroy() {
    window.removeEventListener('afterprint', this.handleAfterPrint);
  },
  methods: {
    async refresh() {
      await this.listStudentEvents()
    },
    async load() {
      if(!this.events.length) {
        await this.listStudentEvents()
      }
      else {
        this.studentEvents.items = this.events
      }

      // Apply filters
      this.studentEvents.items = this.studentEvents.items?.filter((studentEvent) => {
        if(this.filters?.hotel && this.filters.hotel !== studentEvent?.hotelId) { return false }
        if(this.filters?.ensemble && this.filters.ensemble !== this.getApplicationWithSelection(studentEvent?.student?.applications).selection?.ensemble?.id) { return false }
        if(this.filters?.instrument && this.filters.instrument !== this.getApplicationWithSelection(studentEvent?.student?.applications).instrument?.id) { return false }
        return true;
      })
      this.studentEvents.loading = false

      if(!this.logo) {
        this.logo = require('@/assets/images/logo/logo5-blue-white-gold.svg');
      }

      this.$nextTick(() => {
        setTimeout(() => {
          window.print()
        }, 500);
      })
    },
    async listStudentEvents() {
      this.studentEvents.loading = true;
      await getStudentEvents().then(students => {
        this.studentEvents.items = students;
        this.studentEvents.loading = false;
        this.studentEvents.loaded = true;
      })
    },
    getStudentEventsForChaperone(chaperone) {
      return this.sortItems(this.sorting, this.studentEvents.items.filter(studentEvent => studentEvent.chaperoneId === chaperone.id))
    },

    /** Utils **/
    compareString(a, b) {
      const A = (a ?? '').toString();
      const B = (b ?? '').toString();
      // empty strings sort last
      if (!A && !B) return 0;
      if (!A) return 1;
      if (!B) return -1;
      return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare(A, B);
    },

    handleAfterPrint() {
      this.$bvModal.hide(this.id)
    },
  }
}
</script>

<style lang="scss">
@media print {
  .print-logo {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    vertical-align: middle;
  }

  #app {
    visibility: hidden;
    display: none;
  }

  table {
    page-break-after: always;
    break-after: page;

    * {
      font-size: 11px;
    }

    caption {
      display: table-caption;
      margin: 0 auto 2rem auto;
    }

    thead {
      page-break-before:unset!important;
      page-break-inside: unset!important;
      page-break-after:unset!important;
    }
  }

  .modal {
    padding-left: 0 !important;
    position: relative;

    .modal-dialog {
      width: 100vw;
      max-width: none;
      margin: 0;

      span[tabindex] {
        visibility: hidden;
        display: none;
      }

      .modal-content {
        height: 100%;
        border: 0;

        .modal-body {
          overflow-y: visible;

          .b-overlay-wrap {
            overflow: hidden;

            .b-overlay {
              visibility: hidden;
              display: none;
            }
          }
        }
      }

      .modal-footer {
        visibility: hidden;
        display: none;
      }
    }
  }

  .modal-backdrop {
    visibility: hidden;
    display: none;
  }
}
</style>


